import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { FaPlus, FaSave, FaTrash } from 'react-icons/fa';
import { useApp } from '../../context/AppContext';
import {
  getAdminDemandTypes,
  createDemandType,
  updateDemandType,
  deleteDemandType,
} from '../../services/demandTypeDefinitionService';
import AdminSettingsBackLink from '../../components/admin/AdminSettingsBackLink';

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  min-height: 100vh;
  padding: 2rem;
  background: var(--color-bg-secondary);
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const Panel = styled.section`
  padding: 1.5rem;
  border-radius: 16px;
  background: var(--color-bg-primary);
  border: 1px solid var(--color-border-primary);
  box-shadow: var(--shadow-sm);
  overflow-x: auto;
`;

const Title = styled.h1`
  margin: 0 0 0.5rem;
  font-size: 1.5rem;
  font-weight: 650;
  letter-spacing: -0.02em;
  line-height: 1.2;
  color: var(--color-text-primary);
`;

const Hint = styled.p`
  margin: 0 0 1.25rem;
  color: var(--color-text-secondary);
  font-size: 0.9rem;
  line-height: 1.5;
`;

const Form = styled.form`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 1rem;
  align-items: end;
`;

const Label = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  color: var(--color-text-secondary);
  font-size: 0.8rem;
  font-weight: 600;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--color-border-primary);
  border-radius: 10px;
  box-sizing: border-box;
  color: var(--color-text-primary);
  background: var(--color-bg-primary);
`;

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  min-height: 36px;
  padding: 0.5rem 0.85rem;
  border: 1px solid ${({ $danger }) => ($danger ? '#ef4444' : 'var(--color-btn-primary-border)')};
  border-radius: 10px;
  color: ${({ $danger }) => ($danger ? '#ef4444' : 'var(--color-btn-primary-text)')};
  background: ${({ $danger }) => ($danger ? 'transparent' : 'var(--color-btn-primary-bg)')};
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const Table = styled.table`
  width: 100%;
  min-width: 640px;
  border-collapse: collapse;

  th,
  td {
    padding: 0.75rem 0.5rem;
    border-bottom: 1px solid var(--color-border-primary);
    text-align: left;
    vertical-align: middle;
  }

  th {
    color: var(--color-text-secondary);
    font-size: 0.75rem;
    text-transform: uppercase;
  }
`;

const Actions = styled.div`
  display: flex;
  gap: 0.4rem;
  flex-wrap: wrap;
`;

const emptyForm = () => ({ code: '', label: '', sortOrder: 100 });

const DemandTypesAdminPage = () => {
  const { reloadDemandTypes, addToast } = useApp();
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(emptyForm());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getAdminDemandTypes();
      setItems(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error('Erro ao carregar tipos de demanda:', error);
      addToast?.('Erro ao carregar tipos de demanda.', 'error');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    load();
  }, [load]);

  const refresh = async () => {
    await load();
    await reloadDemandTypes?.();
  };

  const handleCreate = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      await createDemandType({
        code: form.code.trim().toUpperCase(),
        label: form.label.trim(),
        sortOrder: Number(form.sortOrder) || 0,
      });
      addToast?.('Tipo de demanda criado.', 'success');
      setForm(emptyForm());
      await refresh();
    } catch (error) {
      addToast?.(error?.response?.data?.message || error?.message || 'Erro ao criar tipo.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const updateRow = async (row) => {
    try {
      await updateDemandType(row.id, {
        label: String(row.label || '').trim(),
        sortOrder: Number(row.sortOrder) || 0,
        active: row.active !== false,
      });
      addToast?.('Tipo de demanda atualizado.', 'success');
      await refresh();
    } catch (error) {
      addToast?.(error?.response?.data?.message || error?.message || 'Erro ao atualizar.', 'error');
    }
  };

  const deleteRow = async (row) => {
    if (!window.confirm(`Remover o tipo de demanda "${row.label}" (${row.code})?`)) return;
    try {
      await deleteDemandType(row.id);
      addToast?.('Tipo de demanda removido.', 'success');
      await refresh();
    } catch (error) {
      addToast?.(error?.response?.data?.message || error?.message || 'Não foi possível remover.', 'error');
    }
  };

  const patchRow = (id, field, value) => {
    setItems((previous) => previous.map((row) => (row.id === id ? { ...row, [field]: value } : row)));
  };

  return (
    <Page>
      <AdminSettingsBackLink />
      <Panel>
        <Title>Configurar tipos de demanda</Title>
        <Hint>Gerencie os tipos disponíveis nos tickets, filtros e vínculos de status.</Hint>
        <Form onSubmit={handleCreate}>
          <Label>
            Código
            <Input
              required
              value={form.code}
              onChange={(event) => setForm((current) => ({ ...current, code: event.target.value }))}
              placeholder="EX.: SUPPORT"
            />
          </Label>
          <Label>
            Rótulo
            <Input
              required
              value={form.label}
              onChange={(event) => setForm((current) => ({ ...current, label: event.target.value }))}
              placeholder="Suporte / Bug"
            />
          </Label>
          <Label>
            Ordem
            <Input
              type="number"
              value={form.sortOrder}
              onChange={(event) => setForm((current) => ({ ...current, sortOrder: event.target.value }))}
            />
          </Label>
          <Button type="submit" disabled={saving}>
            <FaPlus /> Adicionar tipo
          </Button>
        </Form>
      </Panel>
      <Panel>
        {loading ? <p>Carregando...</p> : (
          <Table>
            <thead>
              <tr><th>Código</th><th>Rótulo</th><th>Ordem</th><th>Ativo</th><th>Tickets</th><th>Ações</th></tr>
            </thead>
            <tbody>
              {items.map((row) => (
                <tr key={row.id}>
                  <td>{row.code}</td>
                  <td><Input value={row.label || ''} onChange={(event) => patchRow(row.id, 'label', event.target.value)} /></td>
                  <td><Input type="number" value={row.sortOrder ?? 0} onChange={(event) => patchRow(row.id, 'sortOrder', event.target.value)} /></td>
                  <td><input type="checkbox" checked={row.active !== false} onChange={(event) => patchRow(row.id, 'active', event.target.checked)} /></td>
                  <td>{row.ticketCount ?? 0}</td>
                  <td>
                    <Actions>
                      <Button type="button" onClick={() => updateRow(row)}><FaSave /> Salvar</Button>
                      {!row.system && <Button type="button" $danger onClick={() => deleteRow(row)}><FaTrash /></Button>}
                    </Actions>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Panel>
    </Page>
  );
};

export default DemandTypesAdminPage;
